import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  DuplicateSkillLabelError,
  useCreateSkill,
  useSetSkillRetired,
  useSkills,
  useUpdateSkill,
  type Skill,
} from '@/routes/skills/useSkills';

/**
 * The account's skill catalog (M1-38) — "owner/admin manage the account's
 * skill list (add/retire entries)", verbatim from the AC.
 *
 * <b>It lives at `/people/skills` rather than as a section of `/settings`,
 * where M1-25's relationship-role catalog sits.</b> `/settings` is gated on
 * `account.manage`, which the M0-22 matrix gives to the Owner ALONE — putting
 * this there would have locked out an Admin, one of the two roles the AC names.
 * The route is `users.manage` instead, matching the API, and it is reached
 * from the roster rather than from the nav for the same reason
 * `/technicians/:userId` is: it is a thing you go to from the people you are
 * looking at, not a top-level section of the product.
 */
export function SkillCatalog() {
  const { t } = useTranslation();
  const skills = useSkills(true);
  const create = useCreateSkill();

  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState('');

  function addSkill() {
    const label = newLabel.trim();
    if (!label) {
      return;
    }
    setError('');
    create.mutate(label, {
      onSuccess: () => setNewLabel(''),
      onError: (err) =>
        setError(err instanceof DuplicateSkillLabelError ? err.message : t('skillCatalog.addFailed')),
    });
  }

  return (
    <section className="space-y-4">
      <div>
        <Link to="/people" className="text-sm underline underline-offset-2">
          {t('skillCatalog.backToPeople')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">{t('skillCatalog.heading')}</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('skillCatalog.description')}</p>
      </div>

      {skills.isPending ? (
        <LoadingState label={t('skillCatalog.loading')} />
      ) : skills.isError ? (
        <ErrorState title={t('skillCatalog.loadErrorTitle')} onRetry={() => void skills.refetch()} />
      ) : (
        <div className="space-y-3 rounded-lg border p-4">
          {skills.data.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">{t('skillCatalog.empty')}</p>
          ) : (
            <ul className="space-y-1" data-testid="skill-catalog-list">
              {/* sortOrder round-trips as a string as often as a number — the
                  generated client types int32 as `number | string`, the fourth
                  field in this codebase to need the coercion (latitude,
                  longitude, sortOrder, and the licence counts). */}
              {[...skills.data]
                .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
                .map((skill) => (
                  <SkillRow key={skill.id} skill={skill} />
                ))}
            </ul>
          )}

          <div className="flex items-end gap-2 border-t border-[var(--color-border)] pt-3">
            <label className="block flex-1 text-sm">
              <span className="mb-1 block font-medium">{t('skillCatalog.newLabel')}</span>
              <input
                className={FIELD_CLASS}
                value={newLabel}
                onChange={(event) => setNewLabel(event.target.value)}
              />
            </label>
            <Button type="button" size="sm" disabled={!newLabel.trim() || create.isPending} onClick={addSkill}>
              {t('skillCatalog.add')}
            </Button>
          </div>

          {error ? (
            <p role="alert" className="text-sm text-[var(--color-destructive)]">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}

/**
 * One catalog row — rename inline, retire/restore. Retiring is never a delete:
 * assignments already reference this entry (api/CONVENTIONS.md's M1-38
 * section), so the API offers no delete for this screen to call.
 */
function SkillRow({ skill }: { skill: Skill }) {
  const { t } = useTranslation();
  const update = useUpdateSkill();
  const setRetired = useSetSkillRetired();
  const [label, setLabel] = useState(skill.label);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Moves focus into the field once it appears, without the a11y pitfalls of
  // the declarative `autoFocus` prop that `jsx-a11y/no-autofocus` rejects —
  // this only runs in reaction to the person's own "Rename" click.
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  function save() {
    const trimmed = label.trim();
    if (!trimmed || trimmed === skill.label) {
      setEditing(false);
      setLabel(skill.label);
      return;
    }
    setError('');
    update.mutate(
      { id: skill.id, label: trimmed, sortOrder: Number(skill.sortOrder) },
      {
        onSuccess: () => setEditing(false),
        onError: (err) =>
          setError(err instanceof DuplicateSkillLabelError ? err.message : t('skillCatalog.renameFailed')),
      },
    );
  }

  return (
    <li
      data-testid="skill-catalog-row"
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm"
    >
      {editing ? (
        <span className="flex flex-1 items-center gap-2">
          <label htmlFor={`skill-label-${skill.id}`} className="sr-only">
            {t('skillCatalog.renameLabelFor', { label: skill.label })}
          </label>
          <input
            id={`skill-label-${skill.id}`}
            ref={inputRef}
            className={FIELD_CLASS}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                save();
              }
              if (event.key === 'Escape') {
                setEditing(false);
                setError('');
                setLabel(skill.label);
              }
            }}
          />
          <button type="button" className="text-xs underline underline-offset-2" onClick={save}>
            {t('common.save')}
          </button>
        </span>
      ) : (
        <span className="flex flex-1 flex-wrap items-center gap-2">
          <span className={skill.isActive ? '' : 'text-[var(--color-muted-foreground)] line-through'}>
            {skill.label}
          </span>
          {!skill.isActive ? (
            <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-muted-foreground)]">
              {t('skillCatalog.retiredChip')}
            </span>
          ) : null}
        </span>
      )}

      <span className="flex items-center gap-3">
        {!editing ? (
          <button
            type="button"
            className="text-xs underline underline-offset-2"
            onClick={() => setEditing(true)}
          >
            {t('skillCatalog.rename')}
          </button>
        ) : null}
        <button
          type="button"
          className="text-xs underline underline-offset-2"
          disabled={setRetired.isPending}
          onClick={() => setRetired.mutate({ id: skill.id, retired: skill.isActive })}
        >
          {skill.isActive ? t('skillCatalog.retire') : t('skillCatalog.restore')}
        </button>
      </span>

      {error ? (
        <p role="alert" className="w-full text-sm text-[var(--color-destructive)]">
          {error}
        </p>
      ) : null}
    </li>
  );
}
